import {
  formatDate,
  formatFileSize,
  isCurrentDocument,
} from "../adminReportUtils";
import { PolicyDocument, RegionCode } from "../types";
import {
  ChangeChip,
  CoverageChip,
  DocumentTypeChip,
  ScopeChip,
} from "./documentChips";
import SortableHeader from "./sortableHeader";

interface Column {
  label: string;
  /** Omit to render a non-sortable column. */
  field?: keyof PolicyDocument;
}

const COLUMNS: Column[] = [
  { label: "Document", field: "fileName" },
  { label: "Policy ID", field: "policyId" },
  { label: "Scope", field: "isGlobal" },
  { label: "Type", field: "nodeDocumentType" },
  { label: "Change Type", field: "changeType" },
  { label: "Change Date", field: "changedAt" },
  { label: "Test Coverage" },
];

interface DocumentsTableProps {
  documents: PolicyDocument[];
  region: RegionCode;
  sortColumn: keyof PolicyDocument;
  sortDirection: string;
  onSort: (field: keyof PolicyDocument) => void;
  onDocumentClick: (attachmentId: string) => void;
}

const DocumentsTable = ({
  documents,
  region,
  sortColumn,
  sortDirection,
  onSort,
  onDocumentClick,
}: DocumentsTableProps) => (
  <table className="admin-report__table">
    <thead>
      <tr>
        {COLUMNS.map(({ label, field }) => (
          <SortableHeader
            key={label}
            label={label}
            field={field as string}
            sortColumn={sortColumn as string}
            sortDirection={sortDirection}
            onSort={onSort as (field: string) => void}
          />
        ))}
      </tr>
    </thead>
    <tbody>
      {documents.map((document) => (
        <tr
          key={document.attachmentId}
          className={
            isCurrentDocument(document)
              ? "is-clickable"
              : "is-clickable is-deleted"
          }
          onClick={() => onDocumentClick(document.attachmentId)}
        >
          <td>
            <div className="admin-report__doc-name">{document.fileName}</div>
            <div className="admin-report__doc-sub">
              {[
                document.nodeTitle,
                // formatFileSize(0) reads "1 KB", so an absent size is omitted
                // rather than reported as a real one.
                document.sizeBytes > 0 && formatFileSize(document.sizeBytes),
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </td>
          <td>
            <span className="admin-report__mono">{document.policyId}</span>
          </td>
          <td>
            <ScopeChip region={region} isGlobal={document.isGlobal} />
          </td>
          <td>
            <DocumentTypeChip documentType={document.nodeDocumentType} />
          </td>
          <td>
            <div className="admin-report__chips">
              <ChangeChip changeType={document.changeType} />
            </div>
          </td>
          <td>
            <span className="admin-report__doc-sub">
              {formatDate(document.changedAt) || "—"}
            </span>
          </td>
          <td>
            <CoverageChip testCaseId={document.testCase?.testCaseId} />
          </td>
        </tr>
      ))}
      {!documents.length && (
        <tr>
          <td className="admin-report__table-empty" colSpan={COLUMNS.length}>
            No documents match the current filters.
          </td>
        </tr>
      )}
    </tbody>
  </table>
);

export default DocumentsTable;
